import { useMutation } from '@apollo/client'
import { useCallback } from 'react'
import { graphql } from '~publish/gql'
import type { SavedRepliesQueryQuery, SavedReply } from '~publish/gql/graphql'
import type { SavedReplyId } from '../../types'
import { handleSavedReplyError } from './handleSavedReplyError'
import { SavedRepliesQuery } from './useSavedRepliesQuery'

export type UpdateSavedReply = (params: {
  id: SavedReplyId
  title?: string | null
  content?: string
  hotkeys?: string[] | null
}) => Promise<SavedReply>

export const UpdateSavedReply = graphql(/* GraphQL */ `
  mutation updateSavedReply($input: UpdateSavedReplyInput!) {
    updateSavedReply(input: $input) {
      ... on SavedReplyActionSuccess {
        savedReply {
          id
          organizationId
          title
          content {
            text
          }
          hotkeys
          createdAt
          updatedAt
        }
      }
      ... on MutationError {
        message
      }
    }
  }
`)

/**
 * Hook to update a saved reply.
 * @returns {UpdateSavedReply} Function to update a saved reply.
 * @example
 * const updateSavedReply = useUpdateSavedReply();
 *
 * const handleUpdateSavedReply = async () => {
 *   try {
 *     const updatedSavedReply = await updateSavedReply({ id: '123', title: 'New Saved Reply', content: 'This is a new saved reply' });
 *
 *   } catch (err) {
 *     console.error(err);
 *   }
 * };
 */
export function useUpdateSavedReply(): UpdateSavedReply {
  const [updateSavedReplyMutation] = useMutation(UpdateSavedReply, {
    update(cache, { data }) {
      if (
        !data?.updateSavedReply ||
        data.updateSavedReply.__typename !== 'SavedReplyActionSuccess'
      )
        return

      const updatedItem = data.updateSavedReply.savedReply

      const variables = {
        input: { organizationId: updatedItem.organizationId },
      }

      // Read from the cache with correct variables
      const existingData = cache.readQuery<SavedRepliesQueryQuery>({
        query: SavedRepliesQuery,
        variables,
      })

      if (!existingData) return

      const updatedSavedReplies = existingData.savedReplies.savedReplies.map(
        (savedReply) =>
          savedReply.id === updatedItem.id ? updatedItem : savedReply,
      )

      cache.writeQuery<SavedRepliesQueryQuery>({
        query: SavedRepliesQuery,
        variables,
        data: {
          savedReplies: {
            __typename: 'SavedRepliesResponse',
            savedReplies: updatedSavedReplies,
          },
        },
      })
    },
  })

  const updateSavedReply = useCallback(
    async ({
      id,
      title,
      content,
      hotkeys,
    }: {
      id: SavedReplyId
      title?: string | null
      content?: string
      hotkeys?: string[] | null
    }): Promise<SavedReply> => {
      /* undefined values keep the content with the same value */
      const dataToUpdate = {
        ...(title !== undefined && { title: title === '' ? null : title }),
        ...(hotkeys !== undefined && { hotkeys }),
        ...(content !== undefined && { content: { text: content } }),
      }

      const { data, errors } = await updateSavedReplyMutation({
        variables: {
          input: {
            id,
            savedReply: {
              ...dataToUpdate,
            },
          },
        },
      })

      if (errors || !data) {
        throw new Error(errors?.[0]?.message ?? 'Failed to update saved reply')
      }

      const result = data.updateSavedReply
      if (result.__typename !== 'SavedReplyActionSuccess') {
        handleSavedReplyError({
          typename: result.__typename ?? '',
          operation: 'update',
          element: 'saved reply',
        })
        /* needed only for TS to be happy */
        throw new Error('Unexpected error')
      }

      return result.savedReply
    },
    [updateSavedReplyMutation],
  )

  return updateSavedReply
}
